import { cloneDeep } from "lodash-es";
import { reactive } from "vue";
import * as API from "@/api/API";
import envOptions from "@/helpers/Env";
import { getUserDB } from "@/api/DB";
import {
  AccessDeniedError,
  ModelLimitReachedError,
  ModelNotFoundError,
  NotAuthenticatedError
} from "@core/errors/Errors";

import { useWorkspaceModelsDB } from "./models.db";
import { modelDataLocalAPI } from "./modelVersionAPI";
import { useUser, useUserContext } from "@/api/user.store";
import { showConflictModelPopup } from "@manager/workspacemodels/ModelConflictScreen";
import eventHub from "@/helpers/utility/Events";
import { dateOrNow } from "@/helpers/utility/DateTimeFunctions";
import { useLicense } from "./license.store";

import type { LocalModelInfo } from "@/api/DB";
import type { RequestOptions } from "@/api/API";
import type { Model } from "./Model";

export const modelCloudAPI = {
  async list(requestOptions: RequestOptions<Model[]> = {}) {
    return API.get<Model[]>("/v1/models", requestOptions);
  },

  /**
   * Return a single modelInfo entry
   */
  async get(
    modelId: number | string,
    requestOptions: RequestOptions<Model> = {}
  ) {
    return API.get<Model>(`/v1/models/${modelId}`, requestOptions);
  },

  /**
   * Update one model info on the server.
   */
  async update(
    workspaceId: number,
    modelId: number | string,
    requestOptions: RequestOptions<Model> = {}
  ) {
    return API.patch<Model>(
      `/v1/workspaces/${workspaceId}/models/${modelId}`,
      requestOptions
    );
  },

  /**
   * Create a modelInfo on the server.
   */
  async create(
    modelInfo: { workspace: number; name: string; folder: number },
    requestOptions: RequestOptions<Model> = {}
  ) {
    const workspaceId = modelInfo?.workspace;
    if (workspaceId == null) {
      throw new Error("No workspace id set.");
    }

    console.log("POST to /models", modelInfo);
    return API.post<Model>(`/v1/workspaces/${workspaceId}/models`, {
      ...requestOptions,
      data: {
        ...modelInfo
      }
    });
  },

  /**
   * Destroy a modelInfo on the server
   */
  async destroy(
    workspaceId: number,
    modelId: number | string,
    requestOptions: RequestOptions = {}
  ) {
    return API.destroy(
      `/v1/workspaces/${workspaceId}/models/${modelId}`,
      requestOptions
    );
  },

  /**
   * Copy a modelInfo to another server.
   */
  async copy(
    workspaceId: number,
    modelId: number | string,
    requestOptions: RequestOptions<Model> = {}
  ) {
    return API.post<Model>(
      `/v1/workspaces/${workspaceId}/models/${modelId}/copy`,
      requestOptions
    );
  }
};

export const modelLocalAPI = {
  /**
   * Retrieve a list of modelInfos from the local DB.
   */
  async list(
    requestOptions: RequestOptions<Model[]> & {
      includeDeleted?: boolean;
    } = {}
  ) {
    const modelsDB = getModelsDB();
    const query: {
      workspace?: number | string;
      folder?: number;
      folderSlug?: string;
    } = {};

    let collection;

    // Create Query for filtering by folder
    if (requestOptions?.query?.folder_slug != null) {
      const workspaceId = toIntIfNotUuid(requestOptions.query.workspace);
      const folderSlug = requestOptions.query.folder_slug as string;

      // get the folder the model should be in
      const { user: userRef } = useUser();
      const user = userRef.value;

      if (user == null) {
        console.warn("user not loaded.");
        return [];
      }

      const db = getUserDB(user?.id);
      const workspace = await db.workspaces.get({
        id: workspaceId
      });
      const folders = workspace?.folders ?? [];
      const folder = folders.find((f) => f.slug == folderSlug);

      // load all the models available offline
      if (folder) {
        query.workspace = workspaceId;
        query.folder = folder.id;
      } else if (folderSlug) {
        query.workspace = workspaceId;
        query.folderSlug = folderSlug;
      }

      collection = modelsDB.where(query);
    } else {
      collection = modelsDB.toCollection();
    }

    if (!requestOptions?.includeDeleted) {
      collection = collection.filter((m) => !m.deleted);
    }

    if (
      requestOptions?.query?.search != null &&
      typeof requestOptions.query.search === "string"
    ) {
      const search = requestOptions.query.search;
      collection = collection.filter((m) => {
        return (
          m.name?.toLowerCase().includes(search.toLowerCase()) ||
          m.description?.toLowerCase().includes(search.toLowerCase())
        );
      });
    }

    return await collection.toArray();
  },

  /**
   * Return a single modelInfo entry from the local database.
   */
  async get(modelId: number | string) {
    const modelsDB = getModelsDB();

    const iModelId = toIntIfNotUuid(modelId);

    const model = await modelsDB.where({ id: iModelId }).first();

    if (model == null) {
      throw new ModelNotFoundError();
    }

    console.log(
      `%cfrom local db: model ${iModelId}`,
      "background-color: yellow; padding: 3px 1rem;"
    );

    return model;
  },

  async update(
    workspaceId: number,
    modelId: number | string,
    requestOptions: RequestOptions<Model> & {
      data: Partial<Model | LocalModelInfo>;
    }
  ) {
    const modelsDB = getModelsDB();

    const modelInfo = (requestOptions.data ?? {}) as LocalModelInfo;

    const updatedModel = await modelsDB.updateAndGet(
      modelId,
      cloneDeep(modelInfo)
    );
    console.debug("updated local model", updatedModel);

    return updatedModel;
  },

  /**
   * Create a new entry in the LOCAL model database
   */
  async create(modelInfo: Partial<LocalModelInfo>) {
    const { user } = useUserContext();
    const license = useLicense();
    const workspaceId = modelInfo?.workspace;
    if (user == null) {
      throw new NotAuthenticatedError();
    }
    if (workspaceId == null) {
      throw new Error("No workspace id set.");
    }

    // make sure permissions are set
    if (!modelInfo.permissions) {
      modelInfo.permissions = {
        canDelete: true,
        canDownload: true,
        canFork: true,
        canLock: true,
        canUpdate: true
      };
    }

    const modelsDB = getModelsDB();

    // check user save limits:
    if (license.limits.maxModels != null && workspaceId === user.workspace?.id) {
      const models = await modelsDB.where({ workspace: workspaceId }).toArray();
      if (models.length >= license.limits.maxModels) {
        throw new ModelLimitReachedError(license.limits.maxModels);
      }
    }

    let createdModelInfo = await modelsDB.create(cloneDeep(modelInfo));

    if (!createdModelInfo) {
      throw new Error("Error during model creation");
    }

    try {
      // try uploading the model directly
      // if there's no connection, continue with the temporary local model
      createdModelInfo = await modelLocalAPI.uploadNew(createdModelInfo.id);
    } catch (error) {
      console.warn(error);
    }

    return createdModelInfo;
  },

  /**
   * Delete a local model (soft, triggers synch afterwards)
   */
  async destroy(workspaceId: number, modelId: number | string) {
    const modelsDB = getModelsDB();
    const localModel = await modelsDB.get(modelId);

    if (!localModel) {
      throw new ModelNotFoundError();
    }

    if (localModel.isLocked) {
      throw new AccessDeniedError();
    }

    // Only send the "delete" request to the server
    // if the model is on the server.
    if (localModel.isNew) {
      console.log("model is new, delete it directly");
      await modelLocalAPI.delete(modelId);
    }
    // Mark the local model as deleted and try synching the model
    else {
      await modelsDB.update(modelId, { deleted: Date.now() });

      if (useUser().isSyncEnabled()) {
        modelLocalAPI.loadAndSynch(modelId);
      }
    }

    return true;
  },

  /**
   * Remove a model entry and all its dependencies from the local database.
   * (HARD delete)
   */
  async delete(modelId: number | string) {
    if (typeof modelId !== "string" && typeof modelId !== "number") {
      throw new Error("modelId should be string or number.");
    }
    const modelsDB = getModelsDB();
    await modelsDB.delete(modelId);
    eventHub.emit("db-model-updated", modelId, null);
    console.log("deleted local model", modelId);
    return modelDataLocalAPI.delete(modelId);
  },

  async copy(
    workspaceId: number,
    modelId: number | string,
    requestOptions: RequestOptions<Model>
  ) {
    const modelsDB = getModelsDB();
    const modelInfoToCopy = await modelsDB.get({ id: toIntIfNotUuid(modelId) });

    if (modelInfoToCopy == null) {
      throw new Error("Model does not exist.");
    }

    const copyData: Partial<LocalModelInfo> = { ...modelInfoToCopy };

    const data = requestOptions.data as {
      folderId: number;
      workspaceId: number;
      name: string;
    };
    copyData.folder = data.folderId;
    copyData.workspace = data.workspaceId;
    copyData.name = data.name;
    copyData.lastSynch = null;
    delete copyData.id;

    // create the copied model
    const newModel = await modelLocalAPI.create(copyData);
    if (!newModel) {
      throw new Error("Creating new model failed.");
    }

    const copiedModelData = await modelDataLocalAPI.copy(modelId, newModel.id);
    if (!copiedModelData) {
      throw new Error("Model data copying failed.");
    }

    // save the last version info to the models store,
    // this makes sure synchronization works.
    await modelsDB.update(newModel.id, {
      lastVersion: {
        id: copiedModelData.id,
        created: copiedModelData.created,
        lastSynch: null
      }
    });

    return modelsDB.get({ id: newModel.id });
  },

  /**
   * Create a new model on the server
   */
  async uploadNew(localModelId: number | string) {
    const modelsDB = useWorkspaceModelsDB().getModelsDB();
    const oldLocalId = toIntIfNotUuid(localModelId);
    const localModel = await modelsDB.get(oldLocalId);

    if (!localModel) {
      throw new Error(`local model ${oldLocalId} does not exist.`);
    }

    if (localModel.deleted) {
      console.warn(`won't upload deleted model ${oldLocalId}`);
      return undefined;
    }

    // we can't save uid ancestorModel references online,
    // int modelIds only.
    if (typeof localModel.ancestorModel === "string") {
      localModel.ancestorModel = undefined;
    }

    const newModelInCloud = (await modelCloudAPI.create(
      localModel
    )) as LocalModelInfo;

    console.log("uploaded new model", newModelInCloud.name, newModelInCloud);

    newModelInCloud.isNew = false;
    newModelInCloud.lastSynch = dateOrNow(newModelInCloud.modified);

    // overwrite the local database model entry
    await modelsDB.update(oldLocalId, newModelInCloud);

    if (await modelDataLocalAPI.transfer(oldLocalId, newModelInCloud.id)) {
      await modelDataLocalAPI.upload(newModelInCloud.id);
    }

    eventHub.emit("db-model-updated", oldLocalId, newModelInCloud);

    return newModelInCloud;
  },

  /**
   * Overwrite a model on the server with a local model
   */
  async upload(remoteModel: Model | undefined, localModel: Model) {
    const modelsDB = getModelsDB();
    const workspaceId = remoteModel?.workspace ?? localModel.workspace;
    const modelId = localModel.id;

    try {
      const updatedModel = await modelCloudAPI.update(workspaceId, modelId, {
        data: localModel
      });
      console.log("uploaded", updatedModel);
      const updatedDBModel = await modelLocalAPI.update(workspaceId, modelId, {
        data: {
          ...updatedModel,
          lastSynch: dateOrNow(updatedModel.modified)
        }
      });

      eventHub.emit("db-model-updated", modelId, updatedDBModel);

      return updatedDBModel;
    } catch (_error) {
      const error = _error as { status?: number };
      if (error.status === 404 && remoteModel == null) {
        return modelLocalAPI.uploadNew(modelId);
      }
      if (error.status === 403) {
        if (!remoteModel) {
          throw error;
        }

        // No Right to update?
        // Discard local changes and take the changes from online.
        const updatedDBModel = await modelsDB.updateAndGet(modelId, {
          ...remoteModel,
          lastSynch: dateOrNow(remoteModel.modified)
        });

        eventHub.emit("db-model-updated", modelId, updatedDBModel);

        return updatedDBModel;
      } else {
        throw error;
      }
    }
  },

  /**
   * Downloads the last remote state of a model and
   * synch it with the local state.
   */
  async loadAndSynch(modelId: number | string) {
    const parsedModelId = toIntIfNotUuid(modelId);
    const [localModel, remoteModel] = await Promise.all([
      modelLocalAPI.get(parsedModelId),
      modelCloudAPI.get(parsedModelId)
    ]);
    return await modelLocalAPI.synch(localModel, remoteModel, true);
  },

  syncStatus: reactive({
    synching: false,
    current: "",
    remainingModels: 0,
    modelsToProcess: 0,
    aborting: false
  }),

  /**
   * Synchronize all models
   * @returns the synchronized models
   */
  async syncAll(requestOptions: API.RequestOptions = {}) {
    if (this.syncStatus.synching) {
      return;
    }

    try {
      this.syncStatus.synching = true;
      console.debug("--- Synching models");

      // get local and remote models and pair them up:
      const localModels = await modelLocalAPI.list({
        ...requestOptions,
        includeDeleted: true
      });
      const remoteModels = await modelCloudAPI.list(requestOptions);
      const modelPairs = buildRemoteLocalPairs(localModels, remoteModels);

      this.syncStatus.remainingModels = modelPairs.length;
      this.syncStatus.modelsToProcess = modelPairs.length;

      // synch all modelInfo entries, one after the other.
      // we tried parallel processing, but the amount of data was overloading chrome.
      for (const [localModel, remoteModel] of modelPairs) {
        try {
          this.syncStatus.current = localModel?.name ?? remoteModel?.name ?? "";
          await modelLocalAPI.synch(localModel, remoteModel, false);
        } catch (error) {
          console.warn(error);
        }
        this.syncStatus.remainingModels--;
        if (this.syncStatus.aborting) {
          console.warn("Aborting sync");
          this.syncStatus.synching = false;
          this.syncStatus.aborting = false;
          throw new Error("Sync aborted.");
        }
      }

      // refresh the list, emit updates and return.
      const synchedLocalModels = await modelLocalAPI.list(requestOptions);

      // Batch-update the synch time of all models
      console.log("update synch time of all models");
      const modelsDB = getModelsDB();
      await modelsDB.bulkPut(
        synchedLocalModels.map((m) => ({
          ...m,
          lastSynch: dateOrNow(m.modified)
        }))
      );

      for (const model of synchedLocalModels) {
        model.lastSynch = dateOrNow(model.modified);
        eventHub.emit("db-model-updated", model.id, model);
      }
      console.debug("---synched:", synchedLocalModels);
      console.debug("---");
      return synchedLocalModels;
    } finally {
      this.syncStatus.synching = false;
      this.syncStatus.remainingModels = 0;
    }
  },

  async refreshAndSync(modelId: number | string) {
    const localModel = await this.get(modelId);
    const remoteModel = await modelCloudAPI.get(modelId);
    this.synch(localModel, remoteModel, true);
  },

  async synch(
    localModel?: LocalModelInfo,
    remoteModel?: Model,
    updateSynchTime = true
  ) {
    console.debug(
      `📯 synching model ${localModel?.id ?? remoteModel?.id ?? null}`
    );

    const synchedModel = await this.synchModelInfo(localModel, remoteModel);
    if (synchedModel) {
      await modelDataLocalAPI.synchModelData(synchedModel);

      if (updateSynchTime) {
        await getModelsDB().update(synchedModel.id, {
          lastSynch: dateOrNow(synchedModel.modified),
          modified: synchedModel.modified // ALWAYS update the modified time
        });
      }

      eventHub.emit("db-model-updated", synchedModel.id, synchedModel);
    }

    return synchedModel;
  },

  /**
   *
   * @returns synched local model entries
   */
  async synchModelInfo(localModel?: LocalModelInfo, remoteModel?: Model) {
    if (localModel == null) {
      console.debug("No local model");
      if (remoteModel == null) {
        throw new Error("Cant synch two non-existing models.");
      }

      const modelInfo = await updateLocalModel(remoteModel.id, remoteModel);
      await modelDataLocalAPI.download(remoteModel.workspace, remoteModel.id);
      return modelInfo;
    }
    if (localModel.isNew) {
      return modelLocalAPI.uploadNew(localModel.id);
    }

    const localModelDeleted = localModel.deleted;
    const remoteModelDeleted = remoteModel == null;

    // Both deleted individually?
    // Just delete local model
    if (localModelDeleted && remoteModelDeleted) {
      console.log("delete local model permanently");
      await modelLocalAPI.delete(localModel.id);
      return null;
    }

    let localModeldata = null;
    try {
      localModeldata = await modelDataLocalAPI.get(
        localModel.workspace,
        localModel.id
      );
    } catch (error) {
      console.log(`${localModel.id} has no local data`);
    }

    const localModified = new Date(localModel.modified);
    const localDataModified = localModeldata?.created
      ? new Date(localModeldata.created)
      : null;
    const remoteModified = remoteModel ? new Date(remoteModel.modified) : null;
    const remoteDataModified = remoteModel?.lastVersion?.created
      ? new Date(remoteModel.lastVersion.created)
      : null;

    const lastSynch = new Date(localModel.lastSynch ?? 0);
    const lastDataSynch = localModeldata?.lastSynch
      ? new Date(localModeldata.lastSynch)
      : 0;

    const remoteModelChanged =
      remoteModified == null || remoteModified > lastSynch;
    const remoteModelDataChanged =
      remoteDataModified && remoteDataModified > lastDataSynch; // remote model data was only changed if there is one.
    const localModelChanged =
      localModified > lastSynch || localModel.deleted || false;
    const localModelDataChanged =
      localDataModified && localDataModified > lastDataSynch;

    // BOTH CHANGED:
    if (
      (remoteModelDeleted && (localModelChanged || localModelDataChanged)) ||
      (localModelDeleted && (remoteModelChanged || remoteModelDataChanged)) ||
      (remoteModelChanged && localModelChanged)
      // direct modelData conflicts are handled in synchModelData
    ) {
      // BOTH CHANGED: CONFLICT

      console.warn(
        "conflict",
        localModified,
        remoteModified,
        lastSynch,
        localModel,
        remoteModel
      );

      const chosenAction = await showConflictModelPopup(
        localModel,
        remoteModel
      );

      console.log("chosen action", chosenAction);

      switch (chosenAction) {
        case "remote":
          return updateLocalModel(localModel.id, remoteModel);

        case "local":
          return modelLocalAPI.upload(remoteModel, localModel);

        case "both":
          return modelLocalAPI.resolveConflictWithBoth(localModel, remoteModel);

        default:
          throw new Error("option not defined");
      }
    }

    if (remoteModelChanged) {
      if (remoteModel) {
        return updateLocalModel(remoteModel.id, remoteModel);
      } else {
        console.debug(
          "remote model deleted, delete local model",
          localModel.id
        );
        await modelLocalAPI.delete(localModel.id);
        return null;
      }
    }

    if (localModelChanged) {
      if (localModel.deleted) {
        await deleteRemoteModel(localModel);
        return null;
      } else {
        console.debug(
          "only local model changed, uploading...",
          localModified,
          lastSynch,
          remoteModel,
          localModel
        );
        return modelLocalAPI.upload(remoteModel, localModel);
      }
    }

    // NOTHING changed. Just return the model.

    const modelsDB = getModelsDB();
    if (remoteModel) {
      // Check if there are differences between the localModel and the remoteModel:
      const modelsDifferent = (
        Object.keys(remoteModel) as (keyof Model)[]
      ).some((key) => {
        const different = !areSame(localModel[key], remoteModel[key]);
        if (different) {
          console.log("different", key, localModel[key], remoteModel[key]);
        }
        return different;
      });

      if (modelsDifferent) {
        // update the last synch time before returning.
        const currentLocalModel = await modelsDB.updateAndGet(localModel.id, {
          ...remoteModel,
          lastSynch: dateOrNow(remoteModel.modified)
        });
        return currentLocalModel;
      }

      return localModel;

      // // update the last synch time before returning.
      // const currentLocalModel = await modelsDB.updateAndGet(localModel.id, {
      //   ...remoteModel,
      //   lastSynch: dateOrNow(remoteModel.modified)
      // });
      // return currentLocalModel;
    } else {
      console.warn(`NO remote model for ${localModel.id} to synch.`);
      return modelsDB.get(localModel.id);
    }
  },

  /**
   * Resolve a conflict:
   * KEEP the remote file (no changes)
   * COPY the local file as a new model (like saveAs)
   */
  async resolveConflictWithBoth(
    localModel: LocalModelInfo,
    remoteModel: Model | undefined
  ) {
    // create the new model online
    const newModel = await modelLocalAPI.uploadNew(localModel.id);

    // update the info of the current model
    await updateLocalModel(localModel.id, remoteModel);

    return newModel;
  }
};

export const modelAPI = envOptions.saveLocally ? modelLocalAPI : modelCloudAPI;

/**
 * Save a model in the local database.
 */
async function updateLocalModel(
  modelId: number | string,
  remoteModel: Model | undefined
) {
  const modelsDB = getModelsDB();

  if (remoteModel == null) {
    await modelLocalAPI.delete(modelId);
    return null;
  }

  const updatedModel = await modelsDB.updateAndGet(modelId, {
    ...remoteModel,
    deleted: null,
    lastSynch: dateOrNow(remoteModel.modified)
  });

  return updatedModel;
}

/**
 * Build an array with local models paired with their respective remote models.
 */
function buildRemoteLocalPairs(localModels: Model[], remoteModels: Model[]) {
  const entries = new Map<
    number | string,
    [Model | undefined, Model | undefined]
  >();
  for (const localModel of localModels) {
    entries.set(localModel.id, [localModel, undefined]);
  }
  for (const remoteModel of remoteModels) {
    const entry = entries.get(remoteModel.id);
    if (entry) {
      entry[1] = remoteModel;
    } else {
      entries.set(remoteModel.id, [undefined, remoteModel]);
    }
  }
  return Array.from(entries.values());
}

/**
 * Send a message to the server making sure the model is deleted.
 */
async function deleteRemoteModel(localModel: Model) {
  const workspaceId = localModel.workspace;
  await modelCloudAPI.destroy(workspaceId, localModel.id);
  console.log("deleted remote model", localModel.id);

  return modelLocalAPI.delete(localModel.id);
}

/**
 * Returns the local db workspace table
 */
function getModelsDB() {
  return useWorkspaceModelsDB().getModelsDB();
}

/**
 * Makes sure not to transform uuids
 * @returns The number as int or the uuid
 */
function toIntIfNotUuid(str: string | number) {
  if (typeof str != "string") return str;
  if (str.includes("-")) {
    return str;
  }
  return parseInt(str);
}

/**
 * Compare two objects or values.
 */
function areSame(
  a: object | boolean | string | number | undefined | null,
  b: object | boolean | string | number | undefined | null
) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}
